// Client for the PdfLayout render + email endpoints.
//
// Flow: the cart calls one of these two functions, which builds the request
// body (scene context + base64 screenshots) and POSTs it. The /render path
// returns a Blob (the PDF) for download; the /email path returns a small
// status object.
//
// Unity-screenshot capture lives in the caller — it needs a viewer handle and
// the PdfLayout's slot mapping. See CartModal for the assembly.

export interface PdfRenderRequestImage {
  imageKey: string;
  /** Raw base64 or `data:image/png;base64,...` — server strips either. */
  base64: string;
  contentType?: string;
}

export interface PdfSummaryRowDto {
  productId: string;
  name: string;
  description1?: string;
  description2?: string;
  quantity: number;
  imageUrl?: string;
  /** All of the product's parameter values, so a pdf-product-parameter block
   *  can render any parameter (not just description1/2). */
  parameters?: Record<string, string>;
  /** Catalog category / series, for grouping & sorting. */
  category?: string;
  series?: string;
  /** Designer-assigned group membership. Drives "designer-group" grouping. */
  groupId?: string;
  groupName?: string;
  /** The user's chosen finishes, pre-resolved to display names in the browser
   *  (the renderer has no product-detail cache). One entry per picked surface,
   *  in surface order. Drives the pdf-product-materials block. */
  materials?: PdfRowMaterialDto[];
  /** Formatted prices from the live breakdown (e.g. "1.234,00 kr"). Absent when
   *  the host has no price source — the pdf-product-price block renders nothing. */
  unitPrice?: string;
  lineTotal?: string;
  /** Units covered by an inclusion allowance — shown as a note, not charged. */
  includedQuantity?: number;
}

/** A resolved finish pick for a PDF row — surface label + chosen material name. */
export interface PdfRowMaterialDto {
  surfaceLabel: string;
  materialName: string;
}

export interface PdfSummaryTotalDto { label: string; value: string }
export interface PdfSummaryMetaDto  { label: string; value: string }

export interface PdfSceneSummaryContext {
  projectLabel: string;
  headlineTitle: string;
  subtitle?: string;
  /** Active series picked in the Designer — rendered by a pd-series block. */
  seriesLabel?: string;
  seriesDescription?: string;
  rows: PdfSummaryRowDto[];
  totalUnits: number;
  totals: PdfSummaryTotalDto[];
  meta: PdfSummaryMetaDto[];
  renderedAtUtc: string;
}

export interface PdfEmailFields {
  toEmails: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  subject?: string;
  htmlBody?: string;
  plainTextBody?: string;
  fromEmail?: string;
  fromDisplayName?: string;
  replyToEmail?: string;
}

export interface PdfRenderRequestBody {
  sceneSummary?: PdfSceneSummaryContext;
  images?: PdfRenderRequestImage[];
  email?: PdfEmailFields;
  /** Short language code (e.g. "en", "dk") so layout-authored DVar text renders
   *  translated. Unknown/blank falls back to primary server-side. Scene-summary
   *  content (product/material names) is already resolved in the browser from the
   *  translated catalog, so this only affects the layout's own text. */
  lang?: string;
}

/** POSTs the request and returns the PDF as a Blob ready for download. */
export async function renderPdf(opts: {
  baseUrl?: string;
  pdfLayoutId: string;
  body: PdfRenderRequestBody;
  signal?: AbortSignal;
}): Promise<Blob> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${base}/api/pdf-layout/${encodeURIComponent(opts.pdfLayoutId)}/render`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (!res.ok) {
    const msg = await safeReadError(res);
    throw new Error(`PDF render failed: ${res.status} ${msg}`);
  }
  return res.blob();
}

/** POSTs the request to the email endpoint. Returns when the server has
 *  handed off to its SMTP. */
export async function emailPdf(opts: {
  baseUrl?: string;
  pdfLayoutId: string;
  body: PdfRenderRequestBody;
  signal?: AbortSignal;
}): Promise<{ ok: boolean }> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${base}/api/pdf-layout/${encodeURIComponent(opts.pdfLayoutId)}/email`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (!res.ok) {
    const msg = await safeReadError(res);
    throw new Error(`Email send failed: ${res.status} ${msg}`);
  }
  return res.json();
}

/** Wire shape for the EmailLayout send endpoint. The email is the anchor; a PDF
 *  attachment is optional (pdfLayoutId + pdfRenderPayload — the same body the PDF
 *  render route takes). */
export interface EmailLayoutSendBody {
  toEmails: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  plainTextBody?: string;
  // From EMAIL is locked to the DKIM-signed app-settings address (not sent).
  fromDisplayName?: string;
  replyToEmail?: string;
  lang?: string;
  projectName?: string;
  tokens?: Record<string, string>;
  /** Optional PDF attachment. */
  pdfLayoutId?: string;
  /** The PdfRenderRequestBody forwarded to the PDF render route. */
  pdfRenderPayload?: PdfRenderRequestBody;
  /** Base file name (no extension) for the attached PDF. */
  attachmentName?: string;
}

/** POSTs to the EmailLayout send endpoint. Renders the email body server-side
 *  from the layout (translatable blocks) and, when a PDF is specified, renders +
 *  attaches it. Returns when the server has handed off to its SMTP. */
export async function sendEmailLayout(opts: {
  baseUrl?: string;
  emailLayoutId: string;
  body: EmailLayoutSendBody;
  signal?: AbortSignal;
}): Promise<{ ok: boolean }> {
  const base = (opts.baseUrl ?? "").replace(/\/+$/, "");
  const url = `${base}/api/email-layout/${encodeURIComponent(opts.emailLayoutId)}/send`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });
  if (!res.ok) {
    const msg = await safeReadError(res);
    throw new Error(`Email send failed: ${res.status} ${msg}`);
  }
  return res.json();
}

/** Triggers a browser download of the PDF blob. */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function safeReadError(res: Response): Promise<string> {
  try {
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      const j = await res.json();
      return j?.error ?? JSON.stringify(j);
    }
    return await res.text();
  } catch {
    return res.statusText;
  }
}

// ── Slot-mapping walk + Unity screenshot capture ─────────────────────────

/** One pdf-unity-screenshot block found in the slot mapping. */
export interface UnityScreenshotRequest {
  imageKey: string;
  /** Optional camera-preset id forwarded to Unity. Operator-defined. */
  cameraPreset?: string;
  /** Optional render dimensions in pixels. Defaults are Unity's call. */
  widthPx?: number;
  heightPx?: number;
}

/** Walks a PdfLayout slot mapping (parsed JSON object) and returns one entry
 *  per pdf-unity-screenshot block found, deduped by imageKey. Order matches
 *  block traversal order. */
export function collectUnityScreenshotRequests(
  slotMapping: unknown,
): UnityScreenshotRequest[] {
  if (!slotMapping || typeof slotMapping !== "object") return [];
  const out: UnityScreenshotRequest[] = [];
  const seen = new Set<string>();
  for (const slot of Object.values(slotMapping as Record<string, unknown>)) {
    if (!Array.isArray(slot)) continue;
    for (const block of slot) {
      if (!block || typeof block !== "object") continue;
      const b = block as { block?: string; key?: string; props?: Record<string, unknown> };
      if (b.block !== "pdf-unity-screenshot") continue;
      // Default imageKey to the block's key — matches the server-side renderer.
      const imageKey =
        (b.props?.imageKey as string | undefined) ?? b.key ?? "";
      if (!imageKey || seen.has(imageKey)) continue;
      seen.add(imageKey);
      out.push({
        imageKey,
        cameraPreset: b.props?.cameraPreset as string | undefined,
        widthPx: b.props?.widthPx as number | undefined,
        heightPx: b.props?.heightPx as number | undefined,
      });
    }
  }
  return out;
}

/** Asks Unity for one screenshot. Unity is expected to reply with either a raw
 *  base64 string, a data URL, or a JSON object with one of `{ base64, dataUrl,
 *  png }`. Returns the base64 (with data-URL prefix stripped) or null on
 *  failure — null entries are dropped from the upload payload so the PDF still
 *  renders (with the screenshot's <img> showing a 404 placeholder). */
export async function captureUnityScreenshot(
  callUnity: (method: string, payload?: unknown, timeoutMs?: number) => Promise<string>,
  req: UnityScreenshotRequest,
  timeoutMs = 15000,
): Promise<{ imageKey: string; base64: string; contentType?: string } | null> {
  try {
    const raw = await callUnity(
      "RequestSceneScreenshot",
      {
        imageKey: req.imageKey,
        cameraPreset: req.cameraPreset ?? null,
        widthPx: req.widthPx ?? 1920,
        heightPx: req.heightPx ?? 1080,
      },
      timeoutMs,
    );
    // Unity may reply with a JSON object or a bare string. Tolerate both.
    let base64: string | undefined;
    let contentType: string | undefined;
    try {
      const parsed = JSON.parse(raw);
      base64 = parsed?.base64 ?? parsed?.dataUrl ?? parsed?.png ?? parsed?.image;
      contentType = parsed?.contentType;
    } catch {
      base64 = raw;
    }
    if (!base64 || typeof base64 !== "string") return null;
    return { imageKey: req.imageKey, base64, contentType };
  } catch (err) {
    console.warn("[pdfLayoutClient] screenshot capture failed:", req.imageKey, err);
    return null;
  }
}
